import React from "react";
import {
  createMedicationDetail,
  normalizeMedicationDetail,
} from "../shared/childMedicationHelpers";
import { buildMedicationReviewEditorState } from "./profileMedicationHydration";

export const prepareMedicationDraftSave = ({
  medicationDetails = [],
  medicationDraft = {},
  editingMedicationId = null,
  childId = "",
  createdBy = "",
  savedAt = new Date().toISOString(),
} = {}) => {
  const targetEntry = normalizeMedicationDetail(medicationDraft);
  const entryId = editingMedicationId || targetEntry.id;
  const previousMedication = editingMedicationId
    ? medicationDetails.find((entry) => entry.id === entryId) || null
    : null;
  const savedMedicationDetail = {
    ...targetEntry,
    id: entryId,
    isArchived: false,
    archivedAt: "",
    archivedBy: "",
    effectiveFrom: previousMedication?.effectiveFrom || savedAt,
    effectiveTo: "",
    history: previousMedication
      ? [
          ...(Array.isArray(previousMedication?.history) ? previousMedication.history : []),
          {
            ...previousMedication,
            history: [],
            savedAt: previousMedication.savedAt || previousMedication.createdAt || "",
          },
        ]
      : [],
    syncStatus: "saved",
    savedAt,
  };
  const nextMedicationDetails = editingMedicationId
    ? medicationDetails.map((entry) => (
        entry.id === entryId ? savedMedicationDetail : entry
      ))
    : [...medicationDetails, savedMedicationDetail];

  return {
    targetEntry,
    entryId,
    previousMedication,
    nextMedicationDetails,
    medicationRecord: {
      ...targetEntry,
      id: entryId,
      childId,
      createdBy,
      createdAt: savedAt,
      isArchived: false,
      archivedAt: "",
      archivedBy: "",
      effectiveFrom: previousMedication?.effectiveFrom || savedAt,
      effectiveTo: "",
      history: previousMedication
        ? [
            ...(Array.isArray(previousMedication.history) ? previousMedication.history : []),
            {
              ...previousMedication,
              history: [],
            },
          ]
        : [],
      syncStatus: "saved",
      savedAt,
    },
  };
};

export const prepareMedicationArchive = ({
  medicationDetails = [],
  entry = {},
  childId = "",
  createdBy = "",
  archived = true,
  archivedBy = "",
  now = new Date().toISOString(),
} = {}) => {
  const archivedAt = archived ? now : "";
  const resolvedArchivedBy = archived ? archivedBy : "";
  const nextMedicationDetails = medicationDetails.map((item) => (
    item.id === entry.id
      ? {
          ...item,
          isArchived: archived,
          archivedAt,
          archivedBy: resolvedArchivedBy,
          effectiveTo: archived ? archivedAt : "",
          syncStatus: archived ? "archived" : "saved",
        }
      : item
  ));

  return {
    archivedAt,
    archivedBy: resolvedArchivedBy,
    nextMedicationDetails,
    medicationRecord: {
      ...entry,
      childId,
      createdBy: entry.createdBy || createdBy,
      createdAt: entry.createdAt || now,
      isArchived: archived,
      archivedAt,
      archivedBy: resolvedArchivedBy,
      effectiveTo: archived ? archivedAt : "",
    },
  };
};

export const getMedicationArchiveMessage = (entry = {}, archived = true) => (
  archived
    ? `Archive ${entry.name || "this medication"}? It will stay saved but move out of the active list.`
    : `Unarchive ${entry.name || "this medication"}? It will return to the active list.`
);

export const useProfileMedicationEditor = ({
  canEditMedication = true,
} = {}) => {
  const [medicationDetails, setMedicationDetails] = React.useState([]);
  const [medicationDraft, setMedicationDraft] = React.useState(createMedicationDetail());
  const [editingMedicationId, setEditingMedicationId] = React.useState(null);
  const [isMedicationEditorOpen, setIsMedicationEditorOpen] = React.useState(false);

  const updateMedicationDraft = React.useCallback((field, value) => {
    setMedicationDraft((current) => ({
      ...current,
      [field]: field === "schedules" && Array.isArray(value) ? value : value,
    }));
  }, []);

  const clearMedicationDraft = React.useCallback(() => {
    setMedicationDraft(createMedicationDetail());
    setEditingMedicationId(null);
    setIsMedicationEditorOpen(false);
  }, []);

  const addMedicationDetail = React.useCallback((draft = null) => {
    if (!canEditMedication) {
      return;
    }

    setMedicationDraft(draft ? createMedicationDetail(draft) : createMedicationDetail());
    setEditingMedicationId(null);
    setIsMedicationEditorOpen(true);
  }, [canEditMedication]);

  const editMedicationDetail = React.useCallback((entry) => {
    if (!entry || !canEditMedication) {
      return;
    }

    setEditingMedicationId(entry.id);
    setMedicationDraft(normalizeMedicationDetail(entry));
    setIsMedicationEditorOpen(true);
  }, [canEditMedication]);

  const resetMedicationEditor = React.useCallback(() => {
    setMedicationDetails([]);
    clearMedicationDraft();
  }, [clearMedicationDraft]);

  const loadMedicationDetails = React.useCallback((nextMedicationDetails = [], {
    openEditorOnLoad = false,
    medicationReviewSuggestion = null,
    medicationReviewSource = {},
  } = {}) => {
    const activeMedicationDetails = nextMedicationDetails.filter((entry) => entry?.name && !entry?.isArchived);
    const reviewState = buildMedicationReviewEditorState({
      medicationRecords: nextMedicationDetails,
      suggestion: medicationReviewSuggestion,
      source: medicationReviewSource,
    });
    const hasReviewDraft = Boolean(reviewState.medicationDraft);
    const shouldOpenMedicationEditor = openEditorOnLoad
      && canEditMedication
      && (hasReviewDraft || activeMedicationDetails.length <= 1);

    setMedicationDetails(nextMedicationDetails);
    setEditingMedicationId(
      shouldOpenMedicationEditor
        ? (reviewState.editingMedicationId || (activeMedicationDetails.length === 1 && !hasReviewDraft ? activeMedicationDetails[0].id : null))
        : null
    );
    setMedicationDraft(
      shouldOpenMedicationEditor && hasReviewDraft
        ? reviewState.medicationDraft
        : shouldOpenMedicationEditor && activeMedicationDetails.length === 1
          ? normalizeMedicationDetail(activeMedicationDetails[0])
        : createMedicationDetail()
    );
    setIsMedicationEditorOpen(shouldOpenMedicationEditor);
  }, [canEditMedication]);

  return {
    medicationDetails,
    setMedicationDetails,
    medicationDraft,
    editingMedicationId,
    isMedicationEditorOpen,
    updateMedicationDraft,
    clearMedicationDraft,
    addMedicationDetail,
    editMedicationDetail,
    resetMedicationEditor,
    loadMedicationDetails,
  };
};
